import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Award, Users, Globe, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { mockCompanyInfo, mockStats, mockTeamMembers } from '../mock';
import SEO from '../components/SEO';

const AboutUs = () => {
  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'We deliver enterprise-grade solutions with uncompromising quality and attention to detail'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Our teams work as seamless extensions of your organization, fostering true partnership'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'We stay ahead of technology trends to bring you cutting-edge solutions'
    },
    {
      icon: Award,
      title: 'Reliability',
      description: 'Proven track record of consistent delivery and operational excellence'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="About Us — Our Mission & Global AI Teams | NIVA Digital AI"
        description="Meet NIVA Digital: our mission to deliver world-class AI-enabled engineering teams. Enterprise-grade infrastructure and delivery built from offices in Colorado, USA and Bengaluru, India."
        keywords="about NIVA Digital, infrastructure company, global development center, teams India"
        canonical={`${process.env.REACT_APP_BASE_URL || 'https://nivadigital.io'}/about`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              About <span className="text-red-600">NIVA Digital</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Empowering businesses with world-class AI-enabled teams
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {mockStats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-red-600 mb-2">{stat.value}</p>
                <p className="text-gray-600 text-sm md:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-600 text-lg leading-relaxed">
                <p>
                  {mockCompanyInfo.description}
                </p>
                <p>
                  Our flexible global delivery model ensures seamless integration into client environments—onshore engineers embed directly within US teams for cultural alignment and compliance, while offshore capability provides 24/7 coverage and additional cost leverage, together delivering enterprise-grade quality at 30–40% cost savings with 99.9% uptime.
                </p>
                <p>
                  With a proven track record spanning retail, non-profit, and cloud-native environments, NIVA Digital has consistently delivered faster release cycles, resilient cloud operations, and accelerated AI-enabled teams for clients across industries. Whether the need is a single specialist or a full delivery pod, NIVA scales rapidly—without compromising quality, cultural fit, or security posture.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-red-100 to-red-50 rounded-2xl p-8 shadow-2xl">
                <div className="rounded-xl overflow-hidden mb-6 shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1733195296321-b99d129b09cd?crop=entropy&cs=srgb&fm=jpg&q=85&w=940"
                    alt="Digital globe representing global network and connectivity"
                    className="w-full h-56 object-cover"
                    data-testid="about-global-reach-image"
                    loading="lazy"
                  />
                </div>
                <div className="bg-white rounded-lg p-6 shadow">
                  <h3 className="font-semibold text-xl mb-2">Global Reach</h3>
                  <p className="text-gray-600">Operating from Colorado, USA and Bengaluru, India, we serve clients worldwide with 24/7 support</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-red-600 shadow-xl">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <Target className="text-red-600" size={32} />
                </div>
                <h3 className="text-3xl font-bold mb-4">Our Mission</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  To bridge the talent gap and empower businesses with world-class infrastructure teams that deliver enterprise-grade solutions at competitive rates.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-red-600 shadow-xl">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <Eye className="text-red-600" size={32} />
                </div>
                <h3 className="text-3xl font-bold mb-4">Our Vision</h3>
                <p className="text-gray-600 text-lg leading-relaxed">
                  To become the global leader in infrastructure services, known for innovation, reliability, and transformative partnerships with businesses worldwide.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="border-2 hover:border-red-600 transition-all duration-300 hover:shadow-xl text-center">
                  <CardContent className="pt-8">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-red-600" size={32} />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Offices
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Strategically located to serve you 24/7
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* USA Office */}
            <Card className="border-2 hover:border-red-600 transition-all duration-300 shadow-lg hover:shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="text-red-600" size={32} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">United States</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 font-medium mb-1">Address</p>
                        <p className="text-gray-700">
                          1499 W, 120th Ave Suite 110<br />
                          Westminster, CO 80234
                        </p>
                      </div>
                      <a
                        href="https://g.co/kgs/qm9kpu4"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-red-600 hover:text-red-700 font-medium mt-4 group"
                      >
                        View on Google Maps 
                        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* India Office */}
            <Card className="border-2 hover:border-red-600 transition-all duration-300 shadow-lg hover:shadow-xl">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Globe className="text-red-600" size={32} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">India</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500 font-medium mb-1">Address</p>
                        <p className="text-gray-700">
                          648/A, 4th floor, Indiranagar<br />
                          Bengaluru, Karnataka 560038
                        </p>
                      </div>
                      <a
                        href="https://g.co/kgs/uoE5ytC"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-red-600 hover:text-red-700 font-medium mt-4 group"
                      >
                        View on Google Maps 
                        <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-red-600 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            The next generation of engineering teams is AI-enabled. Join the companies already ahead!
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Get in Touch <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;